/**
 * Centralized state store for the fleet dashboard.
 * Holds in-memory vehicle snapshots, alerts, and fleet overview data.
 * Implements a simple pub/sub pattern: UI components subscribe to changes
 * and re-render when notified.
 *
 * Data flows:
 *   WebSocket events → store.handleWSEvent() → notify listeners → re-render
 *   REST API responses → store.setFleetOverview()/setAlerts() → notify → re-render
 * @module state/store
 */
import type {
	Alert,
	FleetOverview,
	VehicleSnapshot,
	WSEvent,
} from "../types/telemetry.ts";

/** Listener callback — called with no args when store state changes. */
type Listener = () => void;

/**
 * Central state container for the dashboard.
 * Thread-safe (single-threaded JS) — all mutations happen synchronously.
 */
class Store {
	private vehicles: Map<string, VehicleSnapshot> = new Map();
	private alerts: Alert[] = [];

	private getAlertKey(a: Alert): string {
		const isStation = a.type?.startsWith("STATION_");
		if (isStation) {
			let stationName = "";
			const match = a.message.match(/"([^"]+)"/);
			if (match) {
				stationName = match[1];
			} else {
				stationName = a.vehicle_id || "unknown";
			}
			return `STATION|${stationName}|${a.message}`;
		}
		return `VEHICLE|${a.vehicle_id}|${a.message}`;
	}

	private deduplicateAlerts(list: Alert[]): Alert[] {
		const seen = new Set<string>();
		const unique: Alert[] = [];
		for (const a of list) {
			const key = this.getAlertKey(a);
			if (!seen.has(key)) {
				seen.add(key);
				unique.push(a);
			}
		}
		return unique;
	}
	private fleetOverview: FleetOverview = {
		total: 0,
		active: 0,
		idle: 0,
		offline: 0,
		charging: 0,
		utilization: 0,
	};
	private listeners: Set<Listener> = new Set();
	private _activeHours = 0;
	private _idleHours = 0;
	private vehicleNames: Map<string, string> = new Map();
	private notifyRafId: number | null = null;

	/** Accumulated active vehicle-hours (from DB). */
	get activeHours(): number {
		return this._activeHours;
	}
	/** Accumulated idle vehicle-hours (from DB). */
	get idleHours(): number {
		return this._idleHours;
	}

	/** Update utilization from the backend (replaces local value with DB truth). */
	seedUtilization(activeHours: number, idleHours: number): void {
		this._activeHours = activeHours;
		this._idleHours = idleHours;
		this.notify();
	}

	/** Subscribe to state changes. Returns an unsubscribe function. */
	subscribe(listener: Listener): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	private notify(): void {
		if (this.notifyRafId !== null) return; // already scheduled
		this.notifyRafId = window.setTimeout(() => {
			this.notifyRafId = null;
			this.listeners.forEach((l) => {
				l();
			});
		}, 200) as unknown as number;
	}

	/**
	 * Route a WebSocket event to the appropriate state update handler.
	 * Supported event types:
	 *   - 'telemetry.update': Updates vehicle snapshot with latest telemetry
	 *   - 'vehicle.status': Updates a vehicle's operational status
	 *   - 'alert.fired': Prepends a new alert (capped at 200)
	 *   - 'fleet.overview': Replaces fleet statistics
	 */
	handleWSEvent(event: WSEvent): void {
		switch (event.type) {
			case "telemetry.update": {
				const snap = event.payload as VehicleSnapshot;
				if (snap?.vehicle_id) {
					this.vehicles.set(snap.vehicle_id, snap);
					this.recomputeOverview();
				}
				break;
			}
			case "vehicle.status": {
				const data = event.payload as {
					vehicle_id: string;
					status: VehicleSnapshot["status"];
				};
				const snap = this.vehicles.get(data.vehicle_id);
				if (snap) {
					snap.status = data.status;
				}
				break;
			}
			case "alert.fired": {
				const alert = event.payload as Alert;
				const key = this.getAlertKey(alert);
				const existingIndex = this.alerts.findIndex((a) => {
					if (a.state === "RESOLVED") return false;
					return this.getAlertKey(a) === key;
				});

				if (existingIndex !== -1) {
					const existing = this.alerts[existingIndex];
					existing.fired_at = alert.fired_at;
					existing.acked_at = alert.acked_at;
					existing.resolved_at = alert.resolved_at;
					existing.state = alert.state;
					existing.lat = alert.lat;
					existing.lng = alert.lng;
					this.alerts.splice(existingIndex, 1);
					this.alerts.unshift(existing);
				} else {
					this.alerts.unshift(alert);
					if (this.alerts.length > 200) this.alerts.pop();
				}
				break;
			}
			case "fleet.overview": {
				this.fleetOverview = event.payload as FleetOverview;
				break;
			}
		}
		this.notify();
	}

	/** Recompute fleet overview from live vehicle snapshots. */
	private recomputeOverview(): void {
		let active = 0,
			idle = 0,
			offline = 0,
			charging = 0;
		this.vehicles.forEach((snap) => {
			switch (snap.status) {
				case "ACTIVE":
					active++;
					break;
				case "IDLE":
					idle++;
					break;
				case "OFFLINE":
					offline++;
					break;
				case "CHARGING":
					charging++;
					break;
			}
		});
		const total = this.vehicles.size;
		this.fleetOverview = {
			total,
			active,
			idle,
			offline,
			charging,
			utilization: total > 0 ? (active / total) * 100 : 0,
		};
	}

	getVehicles(): Map<string, VehicleSnapshot> {
		return this.vehicles;
	}

	getAlerts(): Alert[] {
		return this.alerts;
	}

	getFleetOverview(): FleetOverview {
		return this.fleetOverview;
	}

	setFleetOverview(overview: FleetOverview): void {
		this.fleetOverview = overview;
		this.notify();
	}

	setAlerts(alerts: Alert[]): void {
		this.alerts = this.deduplicateAlerts(alerts);
		this.notify();
	}

	/** Store real vehicle display names fetched from the REST API. */
	setVehicleNames(vehicles: Array<{ id: string; name: string }>): void {
		vehicles.forEach((v) => {
			this.vehicleNames.set(v.id, v.name);
		});
		this.notify();
	}

	/** Return the human-readable vehicle name, falling back to the vehicle ID. */
	getVehicleName(id: string): string {
		return this.vehicleNames.get(id) ?? id;
	}
}

export const store = new Store();
